import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import type PublicTransition from '@ember/routing/transition';
import { scheduleOnce } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { installMarketplaceSkill } from '@databox/ember-integrations/lib/components/Ai/Skills/installMarketplaceSkill';
import type { GenieAnswerReadyDetail } from '@databox/ember-integrations/lib/components/Navigation/navigation.contract';
import type {
	AiFeatureConfig,
	ChatAction,
	IframeActionPayload,
	InitialMessage,
	OnActionClick,
	ScreenChangeHandler,
	SwitchToClientAccountAsset,
	SwitchToClientAccountPayload,
	ToastType,
	VerifyMetricResult,
} from '@databox/types/domains/ai/chat';
import type {
	ConnectDataSourcePayload,
	GoalActionPayload,
	OpenDataboardPreviewPayload,
	OpenLineagePayload,
	VerifyMetricPayload,
} from '@databox/types/domains/ai/chat-action-payload';
import type { ChatImage } from '@databox/types/domains/ai/chat-image';
import {
	AiTrackingOrigin,
	CardSaveAction,
	ChartAction as ChartActionEnums,
	ChatAction as ChatActionEnums,
	ChatMessageType,
	ChatPromptMode,
	ChatScreen,
	ChatType,
	CrossClientAssetType,
	InputMethodType,
	PromptButtonVariant,
	type ChatMode,
} from '@databox/types/domains/ai/chat.enums';
import type { ConnectorsPromoConfig } from '@databox/types/domains/ai/connector.types';
import type { ContextObjectBase } from '@databox/types/domains/ai/context';
import { ContextObjectType } from '@databox/types/domains/ai/context.enums';
import type { SkillsTab } from '@databox/types/domains/ai/settings.enums';
import type { Skill } from '@databox/types/domains/ai/skill.types';
import type { FeatureGateIntent } from '@databox/types/domains/billing/billing.enums';
import { SettingsTab, type ProfileAnchor } from '@databox/types/domains/settings/settings.enums';

import type { APIData, Service } from 'webapp-types';
import {
	AiChatRouteSource,
	GenieAnalyzeEntryPoint,
	GenieBookACallOrigin,
	GenieLayer,
	SupportChannel,
} from 'webapp/enums/ai-chat';
import { BillingFeature, PopupEvent, TeamSectionItemType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { IntercomCover } from 'webapp/enums/intercom';
import { TrackingEvent } from 'webapp/enums/tracking';
import { hasAgenticIntroLaunched, wasCreatedBeforeAgenticLaunch } from 'webapp/utils/agentic-intro';
import { getTs, MD_BREAKPOINT, redirect } from 'webapp/utils/common';

import type { OpenLineageExplorer } from 'webapp/components/lineage-explorer-host';
import OnboardingPopupAgenticIntroComponent from 'webapp/components/onboarding/popup-agentic-intro';
import OnboardingPopupGenieComponent from 'webapp/components/onboarding/popup-genie';
import OnboardingPopupGenieArtifactsComponent from 'webapp/components/onboarding/popup-genie-artifacts';
import type { AiChatSetupOptions } from 'webapp/controllers/genie-thread-base';
import type GenieChatController from 'webapp/controllers/genie/chat';
import type MobileGenieChatController from 'webapp/controllers/mobile/genie/chat';

import type { Board, TeamSectionItem } from 'webapp/models-custom';
import BoardShare from 'webapp/models-custom/board-share';
import DataPerformanceSummaryBlock from 'webapp/models-custom/data-performance-summary/block';
import DataPerformanceSummaryBoard from 'webapp/models-custom/data-performance-summary/board';
import type Goal from 'webapp/models-custom/goal';
import { EXPIRE_PROGRESS } from 'webapp/models-custom/infolayer-item/base';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type PopupModal from 'webapp/models-custom/popup/modal';

import {
	GENIE_FROM_SPACE_ID_PARAM,
	GENIE_THREAD_ID_PARAM,
} from 'webapp/services/genie-thread-agency-return';
import type { TrackingOriginTypeForPreview } from 'webapp/services/model-manager/boards';
import type { DataPerformanceSummary } from 'webapp/services/model-manager/data-performance-summaries';

const GOAL_UNAVAILABLE_MESSAGE = 'Goal is not available';

// A new `key` restarts the panel's chat; a re-render with the same key starts nothing.
interface PanelConversation {
	key: string;
	initialMessages: InitialMessage[];
	contextItems: ContextObjectBase[];
}

type PopupResult<T = object> = {
	saved: boolean;
	targetLabel: string;
} & T;

export interface SlackHostProps {
	isVisible: boolean;
	isConnected: boolean;
	isDeliveryEnabled: boolean;
	onConnect: () => void;
}

export default class AiChatService extends EmberService {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('leave-guard') declare leaveGuardService: Service.LeaveGuard;
	@service('billing') declare billingService: Service.Billing;
	@service('connect') declare connectService: Service.Connect;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation-main') declare navigationMainService: Service.NavigationMain;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('popup') declare popupService: Service.Popup;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('account') declare accountService: Service.Account;
	@service('genie-thread-agency-return')
	declare genieThreadAgencyReturnService: Service.GenieThreadAgencyReturn;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('socket') declare socketService: Service.Socket;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('intercom') declare intercomService: Service.Intercom;

	@tracked isOpen = false;
	@tracked hasBeenOpened = false;
	@tracked panelConversation: PanelConversation | null = null;

	// One attempt per session. A load failure closes the intro without marking it seen
	// (see popup-agentic-intro.gts) and the queue re-checks after every close and route
	// change - without this guard it would re-open in a loop. The next visit starts fresh.
	private agenticIntroAttempted = false;
	@tracked currentSpaceAccessId: string | null = null;

	@tracked private setScreen: ScreenChangeHandler | null = null;

	@action
	onScreenChange(setScreen: ScreenChangeHandler): void {
		this.setScreen = setScreen;
	}

	@action
	goToHome(): void {
		this.setScreen?.(ChatScreen.HOME);
	}

	get canUseAiChat(): boolean {
		return this.billingService.features.aiChatAssistantEnabled;
	}

	get canUseSkills(): boolean {
		const { features } = this.billingService;

		return features.aiSkillsMarketplaceEnabled || features.aiSkillsCustomEnabled;
	}

	get isAiChatAvailable(): boolean {
		return this.accountService.showAiAgent && !this.currentUserService.isViewer;
	}

	/**
	 * The account's in-app support chat, behind the floating launcher and every Contact support link: the Intercom
	 * messenger on a free or trial plan (where the server allows Intercom), Genie on a paid one. A free or trial
	 * account never gets the Genie launcher: where Intercom isn't allowed (a client space, "Remove chat widget") it
	 * gets none, as before Genie replaced Intercom. Nothing until billing has loaded, so neither launcher flashes.
	 */
	get supportChannel(): SupportChannel {
		if (!this.navigationService.initialRequestsDone) {
			return SupportChannel.NONE;
		}

		if (this.intercomService.isEligible) {
			return SupportChannel.INTERCOM;
		}

		if (this.intercomService.isFreeOrTrialAtLoad) {
			return SupportChannel.NONE;
		}

		return this.isAiChatAvailable ? SupportChannel.GENIE : SupportChannel.NONE;
	}

	get hasSupportChat(): boolean {
		return this.supportChannel !== SupportChannel.NONE;
	}

	@action
	openSupportChat(): void {
		switch (this.supportChannel) {
			case SupportChannel.INTERCOM:
				this.intercomService.show();
				break;
			case SupportChannel.GENIE:
				// On the Genie page or a routine run the user is already in a chat, so the side panel doesn't open over it
				if (!this.navigationService.isGenieChatPage) {
					this.openChat();
				}
				break;
			case SupportChannel.NONE:
				break;
		}
	}

	/**
	 * Whether the user may be sent to Databox's own people: never a white-labeled non-admin or a
	 * reseller's client user. Genie's Book a call card answers the user's own ask, so it shows on every
	 * plan where this holds (a paid plan reaches the contact owner).
	 */
	get hasBookingTarget(): boolean {
		return (
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			!this.currentUserService.isClientUserInPaidClientSpace
		);
	}

	/**
	 * Book a call offered unprompted (Genie's welcome screen, Help → Book a call): free and trial plans
	 * only. Those are the plans `accountService.requestSetupHelpLink` books a call for, so a paid plan
	 * would get the contact-owner link behind a Book a call label.
	 */
	get canBookACall(): boolean {
		return this.hasBookingTarget && this.billingService.isFreeOrTrial;
	}

	/**
	 * The "Need help? Email …" address on Genie's blocking banners (side panel and page). Where Databox's
	 * own address must not show, only the agency's replacement does, and nothing under "Remove chat widget".
	 */
	get supportEmail(): string | undefined {
		const { accountService } = this;

		if (this.hasBookingTarget) {
			return accountService.shownHelpEmail;
		}

		if (accountService.removeChatWidget) {
			return undefined;
		}

		return accountService.replaceSupportEmail && accountService.supportEmail
			? accountService.supportEmail
			: undefined;
	}

	async ensureAvailable(transition: PublicTransition): Promise<void> {
		await this.navigationService.initialRequestsPromise;

		if (this.isAiChatAvailable) {
			return;
		}

		this.genieThreadAgencyReturnService.showErrorIfForeignSourceThread(
			this.genieThreadAgencyReturnService.threadIdFromTransition(transition),
			'You do not have access to Genie in this account.',
		);

		if (!transition.sequence) {
			this.navigationService.replaceRoute('index');
		} else {
			transition.abort();
		}
	}

	redirectIfFeatureGated(feature: BillingFeature): void {
		if (this.billingService.checkIfCan(feature)) {
			return;
		}

		this.billingService.handleLimitations(feature);
		this.navigationService.replaceRoute(
			this.navigationService.isMobileApp ? 'mobile.genie.chat' : 'genie.chat.index',
		);
	}

	get isEligibleForAiChat(): boolean {
		return this.canUseAiChat && this.isAiChatAvailable;
	}

	/**
	 * Genie stays above every popup, like a support chat bubble.
	 * BELOW_POPUPS: a modal opened from the panel would close an undismissable one (e.g. 2FA).
	 */
	get genieLayer(): GenieLayer {
		const topModal = this.popupService.popupsActive.filter((popup) => !popup.isContext).at(-1);

		if (!topModal) {
			return GenieLayer.ABOVE_POPUPS;
		}

		const isDismissable =
			topModal.closeOnOverlayClick || (topModal as PopupModal).showClose || topModal.isDrawer;

		return isDismissable ? GenieLayer.ABOVE_POPUPS : GenieLayer.BELOW_POPUPS;
	}

	private get defaultTrackingPostfix(): 'AIchat' {
		return 'AIchat';
	}

	get trackingOrigin(): AiTrackingOrigin {
		const {
			activatedRoute: route,
			activatedSection: section,
			isArtifacts,
			isRoutines,
			isDesigner,
			isActivatedRouteSharedArtifact,
		} = this.navigationService;

		// Genie pages, then datasets, then pages outside the sidebar sections, then the app section.
		// Routes without a section give `null`.
		switch (true) {
			case route.includes(AiChatRouteSource.GENIE) || isArtifacts || isRoutines:
				return AiTrackingOrigin.GENIE;
			case route.includes(AiChatRouteSource.DATASETS):
				return AiTrackingOrigin.DATASET;
			case isDesigner:
				return AiTrackingOrigin.DESIGNER;
			// Logged-in users get the shared artifact page inside the main app, not the standalone shared artifact app
			case isActivatedRouteSharedArtifact:
				return AiTrackingOrigin.SHARED_ARTIFACT;
			default:
				return section as AiTrackingOrigin;
		}
	}

	get featureConfig(): AiFeatureConfig | undefined {
		return this.buildFeatureConfig();
	}

	get fullTrackingOrigin(): `${AiTrackingOrigin}_AIchat` | AiTrackingOrigin.GENIE {
		if (this.trackingOrigin === AiTrackingOrigin.GENIE) {
			return AiTrackingOrigin.GENIE;
		}
		return `${this.trackingOrigin}_${this.defaultTrackingPostfix}`;
	}

	trackGeniePageView(): void {
		this.analyticsService.page('Genie');
	}

	@action
	openChat(): void {
		this.isOpen = true;
		this.hasBeenOpened = true; // Mark as opened to enable component mounting
		this.intercomService.cover(IntercomCover.GENIE_PANEL);
		this.analyticsService.event(TrackingEvent.AI_CHAT_OPENED, {
			page: this.navigationService.activatedRoute,
			origin: this.trackingOrigin,
		});
	}

	private openGenieWithContext(options: AiChatSetupOptions, shouldReplaceRoute = false): void {
		const routeName = this.navigationService.isMobileApp
			? `mobile.${AiChatRouteSource.GENIE}`
			: AiChatRouteSource.GENIE;

		const controllerName = this.navigationService.isMobileApp
			? 'mobile/genie/chat'
			: 'genie/chat';
		const controller = getOwner(this)!.lookup(`controller:${controllerName}`) as
			GenieChatController | MobileGenieChatController;
		controller.setup(options);

		if (shouldReplaceRoute) {
			this.navigationService.replaceRoute(routeName);

			return;
		}

		this.navigationService.transitionToRoute(routeName);
	}

	// Closes modals as the route change to Genie used to.
	// The mobile app has no side panel, and the Genie page and a routine run are a chat themselves.
	openPanelWithContext(
		options: Omit<PanelConversation, 'key'>,
		{ keepPopups = false }: { keepPopups?: boolean } = {},
	): void {
		if (this.navigationService.isMobileApp || this.navigationService.isGenieChatPage) {
			this.openGenieWithContext(options);
			return;
		}

		if (!keepPopups) {
			void this.popupService.closeAll();
		}

		if (this.panelConversation) {
			this.eventManagerService.trigger(AppEvent.GENIE_PANEL_CONVERSATION_ENDED);
		}

		this.panelConversation = { key: crypto.randomUUID(), ...options };

		if (!this.isOpen) {
			this.openChat();
		}
	}

	// `isOnboardingChat` holds the first exit behind a confirmation and, for the owner, lets the
	// Genie icon reopen the chat.
	openGenieWithOnboardingAnswers(
		prompt: string,
		shouldWrap = true,
		isOnboardingChat = true,
	): void {
		const wrappedPrompt =
			shouldWrap && prompt
				? [
						"I'm new here — a bit about my setup:",
						prompt,
						'Ask me a few questions to understand my needs, then guide me from here.',
					].join('\n\n')
				: prompt;

		this.openGenieWithContext(
			{
				initialMessages: wrappedPrompt
					? [{ role: ChatMessageType.HUMAN, content: wrappedPrompt }]
					: [],
				isOnboardingChat,
			},
			true,
		);
	}

	// `metricNames`: more than one switches the prompt to the whole block.
	openGenieForMetricBlock(
		model: Pick<TeamSectionItem, 'blockId' | 'title'> & { metricNames?: string[] },
		entryPoint = GenieAnalyzeEntryPoint.ANALYZE_BUTTON,
	): void {
		if (!model.blockId) {
			return;
		}

		const metricNames = [...new Set(model.metricNames)];
		const isMultiMetric = metricNames.length > 1;

		this.analyticsService.event(TrackingEvent.AI_BOT_PROMPT_SUBMITTED, {
			origin: 'metric',
			page: this.navigationService.activatedRoute,
			chat: ChatType.NEW,
			inputMethod: InputMethodType.PROMPT_BUTTON,
			inputMethodVariant: PromptButtonVariant.ANALYZE_WITH_GENIE,
			entryPoint,
		});

		this.openPanelWithContext({
			contextItems: [
				{
					objectType: ContextObjectType.BLOCK,
					objectTypeId: model.blockId,
				},
			],
			initialMessages: [
				{
					role: ChatMessageType.AI,
					content: isMultiMetric
						? `Analyzing metrics ${metricNames.map((name) => `<b>${name}</b>`).join(', ')}`
						: `Analyzing metric <b>${model.title}</b>`,
				},
				{
					role: ChatMessageType.HUMAN,
					content: isMultiMetric
						? 'Analyze the performance of these metrics over the recent period. Break down the trend of each one and how they relate to each other. Call out any significant shifts, spikes, or drops and what might be causing them. If there are dimensions or segments worth investigating further, point them out. End with 2–3 actionable recommendations for what to do next.'
						: 'Analyze the performance of this metric over the recent period. Break down the trend. Call out any significant shifts, spikes, or drops and what might be causing them. If there are dimensions or segments worth investigating further, point them out. End with 2–3 actionable recommendations for what to do next.',
				},
			],
		});
	}

	// Doesn't stream: MessageProvider sends the initial message once the Genie thread opens.
	@action
	openGenieWithPrompt(
		message: string,
		origin: AiTrackingOrigin,
		contextItems: ContextObjectBase[] = [],
		selectedAccountIds: number[] = [],
		images: ChatImage[] = [],
		chatMode?: ChatMode,
		promptButtonVariant?: PromptButtonVariant,
	): void {
		const prompt = message.trim();

		// An image-only prompt is valid, as in Genie's own composer.
		if (!prompt && !images.length) {
			return;
		}

		this.analyticsService.event(TrackingEvent.AI_BOT_PROMPT_SUBMITTED, {
			origin,
			page: this.navigationService.activatedRoute,
			chat: ChatType.NEW,
			...(promptButtonVariant
				? {
						inputMethod: InputMethodType.PROMPT_BUTTON,
						inputMethodVariant: promptButtonVariant,
					}
				: { inputMethod: InputMethodType.TYPED }),
			// The property Genie's own composer sends on this event.
			...(chatMode ? { chatMode } : {}),
		});

		this.openGenieWithContext({
			contextItems,
			initialMessages: [
				{
					role: ChatMessageType.HUMAN,
					content: prompt,
					...(images.length ? { images } : {}),
				},
			],
			selectedAccountIds,
			chatMode,
		});
	}

	openGenieForMetricSetup(
		metricSetup: MetricSetup,
		{
			keepPopups = false,
			entryPoint = GenieAnalyzeEntryPoint.ANALYZE_BUTTON,
		}: { keepPopups?: boolean; entryPoint?: GenieAnalyzeEntryPoint } = {},
	): boolean {
		const setupData = metricSetup.metricSetupWithoutFiltersData;
		if (!setupData) {
			return false;
		}

		this.analyticsService.event(TrackingEvent.AI_BOT_PROMPT_SUBMITTED, {
			origin: 'metric',
			page: this.navigationService.activatedRoute,
			chat: ChatType.NEW,
			inputMethod: InputMethodType.PROMPT_BUTTON,
			inputMethodVariant: PromptButtonVariant.ANALYZE_WITH_GENIE,
			entryPoint,
		});

		const metricName = metricSetup.metric?.name ?? 'this metric';

		this.openPanelWithContext(
			{
				contextItems: [
					{
						objectType: ContextObjectType.METRIC_SETUP,
						// backend accepts a JSON-stringified metric setup for METRIC_SETUP type;
						// the base type declares number, so cast via unknown
						objectTypeId: JSON.stringify(setupData) as unknown as number,
					},
				],
				initialMessages: [
					{
						role: ChatMessageType.AI,
						content: `Analyzing metric <b>${metricName}</b>`,
					},
					{
						role: ChatMessageType.HUMAN,
						content:
							'Analyze the performance of this metric over the recent period. Break down the trend. Call out any significant shifts, spikes, or drops and what might be causing them. If there are dimensions or segments worth investigating further, point them out. End with 2–3 actionable recommendations for what to do next.',
					},
				],
			},
			{ keepPopups },
		);

		return true;
	}

	// `keepPopups`: the board preview's own Genie input keeps the preview open next to the panel.
	openGenieForBoard(
		model: Board,
		prompt?: string,
		{
			keepPopups = false,
			entryPoint = GenieAnalyzeEntryPoint.ANALYZE_BUTTON,
		}: { keepPopups?: boolean; entryPoint?: GenieAnalyzeEntryPoint } = {},
	): void {
		let content = `Analyze the performance of this dashboard. For each metric, evaluate the recent trend and flag anything unexpected — spikes, drops, or stagnation. Identify connections between metrics that might explain what's happening. Highlight the 2–3 biggest risks or opportunities and recommend specific next steps.`;

		if (prompt) {
			content = prompt;
		}

		this.analyticsService.event(TrackingEvent.AI_BOT_PROMPT_SUBMITTED, {
			origin: 'databoard',
			page: this.navigationService.activatedRoute,
			chat: ChatType.NEW,
			inputMethod: prompt ? InputMethodType.TYPED : InputMethodType.PROMPT_BUTTON,
			...(prompt
				? {}
				: { inputMethodVariant: PromptButtonVariant.ANALYZE_WITH_GENIE, entryPoint }),
		});

		this.openPanelWithContext(
			{
				contextItems: [
					{
						objectType: ContextObjectType.DATABOARD,
						objectTypeId: model.numericId!,
					},
				],
				initialMessages: [
					{
						role: ChatMessageType.AI,
						content: `Analyzing dashboard <b>${model.name}</b>`,
					},
					{
						role: ChatMessageType.HUMAN,
						content,
					},
				],
			},
			{ keepPopups },
		);
	}

	// False when Genie can't start here, so the indicator falls back to its summary popup.
	openGenieForPerformanceSummary(model: DataPerformanceSummary): boolean {
		// The side panel is part of the main app only.
		if (!this.isEligibleForAiChat || !this.navigationService.isMainApp) {
			return false;
		}

		if (model instanceof DataPerformanceSummaryBoard) {
			const board = model.isTypeBoardPreview
				? this.mmBoardsService.findModel(String(model.object?.boardId))
				: model.object;

			if (!board?.numericId) {
				return false;
			}

			this.openGenieForBoard(board, undefined, {
				keepPopups: true,
				entryPoint: GenieAnalyzeEntryPoint.PERFORMANCE_INDICATOR,
			});
			return true;
		}

		if (model instanceof DataPerformanceSummaryBlock && !model.previewHash) {
			const blockId = model.object?.numericId;

			if (!blockId) {
				return false;
			}

			this.openGenieForMetricBlock(
				{ blockId, title: model.title, metricNames: model.trackingMetricsNames },
				GenieAnalyzeEntryPoint.PERFORMANCE_INDICATOR,
			);
			return true;
		}

		return (
			!!model.metricSetupData &&
			this.openGenieForMetricSetup(new MetricSetup(model.metricSetupData), {
				keepPopups: true,
				entryPoint: GenieAnalyzeEntryPoint.PERFORMANCE_INDICATOR,
			})
		);
	}

	submitPromptFromAiSummary(options: {
		model: DataPerformanceSummary;
		promptText: string;
		origin: AiTrackingOrigin;
	}) {
		this.analyticsService.event(TrackingEvent.AI_BOT_PROMPT_SUBMITTED, {
			origin: options.origin,
			page: this.navigationService.activatedRoute,
			chat: ChatType.NEW,
			inputMethod: InputMethodType.TYPED,
		});

		const trendEmoji: Record<string, string> = {
			positive: '🟢',
			neutral: '🟡',
			negative: '🔴',
		};
		const title = `${options.model.title} ${trendEmoji[options.model.trend] ?? '⚪'} <b>${String(options.model.trendDescription)}</b>`;

		this.openPanelWithContext({
			contextItems: [
				{
					objectType: ContextObjectType.PERFORMANCE_SUMMARY,
					objectTypeId: options.model.activeSummary?.id || 0,
				},
			],
			initialMessages: [
				{
					role: ChatMessageType.AI,
					title,
					content: options.model.activeSummary?.text || '',
				},
				{
					role: ChatMessageType.HUMAN,
					content: options.promptText,
				},
			],
		});
	}

	@action
	onUserClose(): void {
		this.analyticsService.event(TrackingEvent.AI_CHAT_CLOSED, {
			page: this.navigationService.activatedRoute,
			origin: this.trackingOrigin,
		});
		this.close();
	}

	@action
	closeByRouteChange(): void {
		this.close();
		this.hasBeenOpened = false;
		this.eventManagerService.trigger(AppEvent.GENIE_PANEL_CONVERSATION_ENDED);
	}

	@action
	setSpaceAccessId(spaceAccessId: string | null): void {
		this.currentSpaceAccessId = spaceAccessId;
	}

	getThreadId: (() => string | null) | null = null;

	@action
	onGetThreadId(getThreadId: () => string | null): void {
		this.getThreadId = getThreadId;
	}

	// The thread the routine run page shows. Its own field: the panel and the full-screen page register
	// through `onGetThreadId`, and neither may clobber the other.
	private getRunThreadId: (() => string | null) | null = null;

	registerRunThreadIdGetter = (getter: () => string | null): void => {
		this.getRunThreadId = getter;
	};

	clearRunThreadIdGetter = (): void => {
		this.getRunThreadId = null;
	};

	// --- Thread & artifact URL sync ---

	// The ids currently reflected in the URL. Single source of the loop guard: only the routes
	// (via applyUrlThread / applyUrlArtifact) and the React callbacks below write them.
	// Tracked because the nav sidebar re-renders off it through `activeThreadId` below.
	@tracked private currentUrlThreadId: string | null = null;
	private currentUrlArtifactId: string | null = null;

	// The URL-synced thread id, exposed for the nav sidebar's selected-row highlight. Deliberately NOT named
	// `activeThreadId` — `threadId` below is React's live active thread, and the two can disagree in the window
	// between React creating a thread and the URL sync completing.
	get urlThreadId(): string | null {
		return this.currentUrlThreadId;
	}

	// The URL-synced routine id, for the nav sidebar's selected-row highlight on the Routines section.
	// Only the routines routes write it. Deliberately NOT part of the thread/artifact sync loop below:
	// routines carry no controller query-param to mirror and never drive a transition of their own.
	@tracked private currentUrlRoutineId: string | null = null;

	get urlRoutineId(): string | null {
		return this.currentUrlRoutineId;
	}

	// Guarded for the same reason as applyUrlThread: @tracked has no equality bailout, so an unchanged id
	// would still rebuild the whole nav prop object.
	applyUrlRoutine(routineId: string | null): void {
		if (this.currentUrlRoutineId !== routineId) {
			this.currentUrlRoutineId = routineId;
		}
	}

	private get genieController(): GenieChatController {
		return getOwner(this)!.lookup('controller:genie.chat') as GenieChatController;
	}

	private get mobileGenieController(): MobileGenieChatController {
		return getOwner(this)!.lookup('controller:mobile/genie/chat') as MobileGenieChatController;
	}

	applyUrlThread(threadId: string | null): void {
		// Guarded: @tracked has no equality bailout, and genie.chat re-runs setupController on every `chat`
		// query-param change (refreshModel), which would otherwise rebuild the whole nav prop object for an
		// unchanged id.
		if (this.currentUrlThreadId !== threadId) {
			this.currentUrlThreadId = threadId;
		}

		this.genieController.threadId = threadId;
	}

	// Same, for the genie.chat.thread.artifact route's `:artifact_id`.
	applyUrlArtifact(artifactId: string | null): void {
		this.currentUrlArtifactId = artifactId;
		this.genieController.artifactId = artifactId;
	}

	private pendingUrlSyncReplace = false;

	// Thread and artifact changes routinely land in the same tick — switching threads closes the
	// open artifact, so React reports both — and two independent transitions would race. Both
	// callbacks only record their id and schedule this, which coalesces them (scheduleOnce dedupes
	// on target+method) into the single transition the combined state implies.
	private scheduleGenieUrlSync(replace = false): void {
		this.pendingUrlSyncReplace ||= replace;
		scheduleOnce('actions', this, this.performGenieUrlSync);
	}

	private performGenieUrlSync(): void {
		const replace = this.pendingUrlSyncReplace;
		this.pendingUrlSyncReplace = false;

		// /m has no thread routes, so the mobile controller's controlled threadId stands in for the URL.
		if (this.navigationService.isMobileApp) {
			if (this.mobileGenieController.threadId !== this.currentUrlThreadId) {
				this.mobileGenieController.threadId = this.currentUrlThreadId;
			}
			return;
		}

		if (!this.currentUrlThreadId) {
			this.transitionGenie(replace, 'genie');
			return;
		}

		if (this.currentUrlArtifactId) {
			this.transitionGenie(
				replace,
				'genie.chat.thread.artifact',
				this.currentUrlThreadId,
				this.currentUrlArtifactId,
			);
			return;
		}

		this.transitionGenie(replace, 'genie.chat.thread', this.currentUrlThreadId);
	}

	// Only ever mirrors a thread/artifact switch React already made (and guarded against dropping
	// unsaved artifact edits itself), so the Ember-side leave guard lets it through.
	private transitionGenie(replace: boolean, routeName: string, ...params: string[]): void {
		this.leaveGuardService.withoutGuard(() =>
			replace
				? this.navigationService.replaceRoute(routeName, ...params)
				: this.navigationService.transitionToRoute(routeName, ...params),
		);
	}

	// Called by React (via onActiveThreadChange) when the active thread changes.
	@action
	onThreadChange(threadId: string | null): void {
		this.rememberOnboardingThread(threadId);

		if (threadId === this.currentUrlThreadId) {
			return;
		}

		this.currentUrlThreadId = threadId;
		// A different thread never carries over the previous thread's artifact segment.
		this.currentUrlArtifactId = null;

		this.scheduleGenieUrlSync();
	}

	// Persisted for the owner so the Genie icon can reopen it (navigation-main resumeThreadId).
	private rememberOnboardingThread(threadId: string | null): void {
		const controller = this.genieController;

		if (!threadId || !controller.isOnboardingChat || controller.onboardingThreadId) {
			return;
		}

		controller.onboardingThreadId = threadId;

		if (this.currentUserService.isOwner && !this.userSettingsService.onboardingChatThreadId) {
			this.userSettingsService.onboardingChatThreadId = threadId;
		}
	}

	// Called by React (via onActiveArtifactChange) whenever the open artifact panel changes —
	// deep link, in-chat tile click, auto-open, or close.
	@action
	onArtifactChange(artifactId: string | null): void {
		if (artifactId === this.currentUrlArtifactId) {
			return;
		}
		this.currentUrlArtifactId = artifactId;

		this.scheduleGenieUrlSync();
	}

	@action
	onThreadNotFound(): void {
		if (
			!this.genieThreadAgencyReturnService.showErrorIfForeignSourceThread(
				this.currentUrlThreadId,
				'This conversation is not available in this account.',
			)
		) {
			this.infolayerService.error('This conversation is no longer available.');
		}
		this.currentUrlThreadId = null;
		this.currentUrlArtifactId = null;

		this.scheduleGenieUrlSync();
	}

	// The artifact id in the URL does not belong to this thread — fall back to the bare thread.
	// Replaces rather than pushes, so the dead link doesn't sit in history for Back to re-trigger.
	@action
	onArtifactNotFound(): void {
		this.currentUrlArtifactId = null;

		this.scheduleGenieUrlSync(true);
	}

	// FeatureGateIntent mirrors BillingFeature's values; its one intent without a twin,
	// ONLY_ADMINS_CONNECT, is not a Genie gate.
	@action
	onUpgrade(intent?: FeatureGateIntent): void {
		this.billingService.handleLimitations(
			intent ? (intent as string as BillingFeature) : BillingFeature.SHARING,
		);
	}

	/**
	 * The single "Add credits" exit for Genie — the settings card, both composer banners and the
	 * out-of-credits dialog all land here. Billing owns which popup opens; Genie never branches.
	 */
	@action
	onAddCredits(): void {
		this.billingService.handleLimitations(BillingFeature.AI_CREDITS, 'genie_chat');
	}

	get threadId(): string | null {
		return this.getThreadId?.() ?? null;
	}

	private buildFeatureConfig(): AiFeatureConfig | undefined {
		const isMobile = this.navigationService.isMobileApp;
		const isDemoUser = this.currentUserService.isDemo;
		const isFreeUser = !this.canUseAiChat || isDemoUser;

		if (!isMobile && !isFreeUser) {
			return;
		}

		return {
			allowedActions: isFreeUser ? [] : [CardSaveAction.SAVE_PREVIEW_METRIC],
			allowedSaveActions: [],
			// Free users keep their history, as on the Genie page, whose navigation lists their chats.
			threads: { canDelete: !isMobile && !isFreeUser, showControls: true },
			composer: { isEnabled: !isFreeUser, canUpgrade: !isDemoUser },
			disclaimer: { isVisible: !isFreeUser },
			artifacts: { canDownload: !isMobile },
		};
	}

	private viewMetricInsights({
		metricSetup,
		dateRange,
		visualization,
	}: APIData.GenerativeAI.AiChat.MetricInsightsDrawerAction) {
		this.metricDetailsService.openMetricDetailsDrawer(
			{
				metricSetup: new MetricSetup({ ...metricSetup }),
				dateRange,
				visualization,
			},
			`${this.trackingOrigin}_${this.defaultTrackingPostfix}`,
		);
	}

	private openBoardPreview({ board }: OpenDataboardPreviewPayload) {
		if (!board?.id) {
			return;
		}

		const boardModel = this.mmBoardsService.findModel(String(board.id));

		if (!boardModel) {
			return;
		}

		if (this.navigationService.isMobileApp) {
			this.navigationService.transitionToRoute('mobile.board', boardModel.token);
			return;
		}

		const trackingOrigin: TrackingOriginTypeForPreview = `${this.trackingOrigin}_${this.defaultTrackingPostfix}`;

		this.mmBoardsService.openPreview(boardModel, trackingOrigin);
	}

	private openReportPreview(reportId: string) {
		const reportModel = this.mmReportsService.findModel(reportId);

		if (reportModel) {
			this.mmReportsService.openPreviewPopup(reportModel);
		}
	}

	private openLoopPreview(loopId: string) {
		const loopModel = this.mmBoardLoopsService.findModel(loopId);

		if (loopModel) {
			this.mmBoardLoopsService.openPreviewPopup(loopModel);
		}
	}

	private openScheduleSnapshotPopup({
		board,
	}: IframeActionPayload): Promise<PopupResult<{ boardId: string | null }>> {
		const boardId = board?.id ? String(board.id) : undefined;
		return new Promise((resolve, reject) => {
			if (!boardId) {
				reject();
				return;
			}

			const result: PopupResult<{ boardId: string | null }> = {
				saved: false,
				targetLabel: '',
				boardId: null,
			};

			const board = this.mmBoardsService.findModel(boardId);
			if (board) {
				const boardShare = this.mmBoardSharesService.createModelForBoard(board);

				const trackingOrigin: TrackingOriginTypeForPreview = `${this.trackingOrigin}_${this.defaultTrackingPostfix}`;

				const popup = this.mmNotificationsService.openEditPopup(
					boardShare,
					trackingOrigin,
					false,
					{
						onSave: (savedItem: unknown) => {
							if (savedItem instanceof BoardShare) {
								Object.assign(result, {
									saved: true,
									targetLabel: 'scheduled dashboards',
									boardId: board.id,
								});
							}
						},
					},
				);

				if (popup) {
					popup.on(PopupEvent.CLOSING, this, () => resolve(result));
				} else {
					resolve(result);
				}
			}
		});
	}

	private openAddToReportPopup({
		board,
	}: IframeActionPayload): Promise<PopupResult<{ reportId: string | null }>> {
		return new Promise((resolve, reject) => {
			if (!board?.id) {
				reject();
			}

			const result: PopupResult<{ reportId: string | null }> = {
				saved: false,
				targetLabel: '',
				reportId: null,
			};

			const boardModel = this.mmBoardsService.findModel(String(board.id));

			if (boardModel) {
				const popup = this.mmBoardsService.addToReport(
					boardModel,
					this.fullTrackingOrigin,
					(reportName: string, reportId: string) => {
						Object.assign(result, {
							saved: true,
							targetLabel: reportName,
							reportId,
						});
					},
					{ openInNewTab: true },
				);

				popup?.on(PopupEvent.CLOSING, this, () => {
					resolve(result);
				});
			}
		});
	}

	private openAddToLoopPopup({
		board,
	}: IframeActionPayload): Promise<PopupResult<{ loopId: string | null }>> {
		return new Promise((resolve, reject) => {
			if (!board?.id) {
				reject();
			}

			const result: PopupResult<{ loopId: string | null }> = {
				saved: false,
				targetLabel: '',
				loopId: null,
			};

			const boardModel = this.mmBoardsService.findModel(String(board.id));

			if (boardModel) {
				this.analyticsService.event(TrackingEvent.ADD_TO_LOOP, {
					origin: this.fullTrackingOrigin,
				});

				void this.mmBoardsService.addToLoop(
					boardModel,
					this.fullTrackingOrigin,
					(loopName: string, loopId: string) => {
						Object.assign(result, {
							saved: true,
							targetLabel: loopName,
							loopId,
						});
						resolve(result);
					},
					{ openInNewTab: true },
				);
			}
		});
	}

	private openShareDataboardPopup({ board }: IframeActionPayload) {
		if (!board?.id) {
			return;
		}

		const boardModel = this.mmBoardsService.findModel(String(board.id));
		const trackingOrigin = this.fullTrackingOrigin;

		if (boardModel) {
			this.analyticsService.event(TrackingEvent.DATABOARD_SHARE_POPUP_OPEN, {
				origin: trackingOrigin,
				type: 'databoard',
			});

			this.mmBoardsService.openSharePopup({ model: boardModel, trackingOrigin });
		}
	}

	private openAddToDataboardPopup({
		metricSetup,
		dateRange,
	}: APIData.GenerativeAI.AiChat.OpenAddToDataboardPopupAction): Promise<
		PopupResult<{ boardId: string | null }>
	> {
		return new Promise((resolve) => {
			const result: PopupResult<{ boardId: string | null }> = {
				saved: false,
				targetLabel: '',
				boardId: null,
			};

			const popup = this.mmBlocksService.openCreateFromMetricSetupPopup({
				metricSetup: new MetricSetup({ ...metricSetup }),
				trackOrigin: `${this.trackingOrigin}_${this.defaultTrackingPostfix}`,
				createdNow: false,
				dateRange,
				openInNewTab: true,
				onBlockCreated: (boardName: string, boardId: string | null) => {
					Object.assign(result, {
						saved: true,
						targetLabel: boardName,
						boardId,
					});
				},
			});

			popup.on(PopupEvent.CLOSING, this, () => resolve(result));
		});
	}

	private openSetNewGoalPopup({
		metricSetup,
	}: APIData.GenerativeAI.AiChat.OpenSetNewGoalAction): Promise<
		PopupResult<{ goalId: string; hasChanged: boolean }>
	> {
		return new Promise((resolve) => {
			const result: PopupResult<{ goalId: string; hasChanged: boolean }> = {
				saved: false,
				targetLabel: '',
				goalId: '',
				hasChanged: false,
			};

			const emberMetricSetup = new MetricSetup({ ...metricSetup });
			const originalSetupKey = emberMetricSetup.uniqueSetupDataKey;

			const popup = this.mmGoalsService.openNewPopupForMetricSetup(
				emberMetricSetup,
				`${this.trackingOrigin}_${this.defaultTrackingPostfix}`,
				(savedGoal: Goal) => {
					Object.assign(result, {
						saved: true,
						targetLabel: savedGoal.title,
						goalId: savedGoal.id!,
						hasChanged:
							originalSetupKey !== savedGoal.mainMetricSetup.uniqueSetupDataKey,
					});
					// Resolve here because the goal create popup closes and reopens as edit popup.
					// The CLOSING event on the create popup fires before onGoalSaved is called.
					resolve(result);
				},
			);

			if (!popup) {
				resolve(result);
				return;
			}
		});
	}

	private openAddToSpacePopup({
		metricSetupData,
	}: APIData.GenerativeAI.AiChat.OpenAddToSpacePopupAction): Promise<
		PopupResult<{ teamId: string }>
	> {
		return new Promise((resolve) => {
			const result = { saved: false, targetLabel: '', teamId: '' };

			const popup = this.mmTeamSectionsService.openAddPopup({
				objectType: TeamSectionItemType.METRIC,
				metricSetupData: [metricSetupData],
				onItemAdded: (teamName: string, teamId: string) => {
					Object.assign(result, {
						saved: true,
						targetLabel: teamName,
						teamId,
					});
				},
			});

			popup.on(PopupEvent.CLOSING, this, () => resolve(result));
		});
	}

	installMarketplaceSkill(slug: string): Promise<Skill> {
		return installMarketplaceSkill(
			{ spaceId: this.accountService.id, userId: Number(this.currentUserService.id) },
			slug,
		);
	}

	@tracked settingsSkillsTab: SkillsTab | null = null;

	@action
	openSettings(tab: SettingsTab, anchor?: ProfileAnchor | null): void {
		this.navigationService.updateQueryParams({ customize: tab, anchor: anchor ?? null });
	}

	@action
	onNavigateToSettings(section?: SettingsTab, skillsTab?: SkillsTab): void {
		this.settingsSkillsTab = skillsTab ?? null;
		this.openSettings(section ?? SettingsTab.PERSONALIZATION);
	}

	@action
	onNavigateToProfile(anchor?: ProfileAnchor): void {
		this.settingsSkillsTab = null;
		this.openSettings(SettingsTab.PROFILE, anchor);
	}

	@action
	onNavigateToCompany(): void {
		this.settingsSkillsTab = null;
		this.openSettings(SettingsTab.COMPANY);
	}

	@action
	closeSettings(): void {
		this.settingsSkillsTab = null;

		this.navigationService.updateQueryParams({ customize: null, anchor: null });
	}

	@action
	closeSettingsAndGoTo(routeName: string): void {
		this.settingsSkillsTab = null;

		this.navigationService.transitionToRoute(routeName, {
			queryParams: { customize: null, anchor: null },
		});
	}

	/**
	 * The long-lived AI Pusher relay is owned by SocketService (established once at app boot,
	 * independent of whether Genie has ever been opened) - this just forwards the bound
	 * subscribe function through as a host prop. Its listeners also get goal.updated, built by
	 * SocketService from data-channel bgEvent goal changes rather than relayed by ai-bff.
	 */
	get subscribeToAiEvents(): Service.Socket['subscribeToAiEvents'] {
		return this.socketService.subscribeToAiEvents;
	}

	@action
	onStartChat(prompt?: string, mode: ChatPromptMode = ChatPromptMode.SUBMIT): void {
		if (prompt && mode === ChatPromptMode.PREFILL) {
			this.openGenieWithContext({ initialPrompt: prompt });

			return;
		}

		if (prompt) {
			this.analyticsService.event(TrackingEvent.AI_BOT_PROMPT_SUBMITTED, {
				origin: 'skills',
				page: this.navigationService.activatedRoute,
				chat: ChatType.NEW,
				inputMethod: InputMethodType.PROMPT_BUTTON,
			});
		}

		this.openGenieWithContext({
			initialMessages: prompt ? [{ role: ChatMessageType.HUMAN, content: prompt }] : [],
		});
	}

	@action
	connectSlack(): void {
		this.currentUserService.openSlackAuthorizationPopup();
	}

	/**
	 * Slack reaches React as host props rather than as an API call: the OAuth popup and the channel
	 * picker already live here, and rebuilding either on the React side would fork behaviour that the
	 * notification settings already own.
	 */
	get slackHostProps(): SlackHostProps {
		return {
			isVisible: this.accountService.showSlackIntegration,
			isConnected: this.currentUserService.isSlackConnected,
			isDeliveryEnabled: this.currentUserService.isSlackEnabled,
			onConnect: this.connectSlack,
		};
	}

	get connectorsPromoHostProps(): ConnectorsPromoConfig {
		return {
			isVisible:
				!this.navigationService.isMobileApp &&
				!this.accountService.isDemoAccount &&
				!this.userSettingsService.hasDismissedGenieConnectorsPromo,
			onDismiss: this.dismissConnectorsPromo,
		};
	}

	@action
	dismissConnectorsPromo(): void {
		this.userSettingsService.hasDismissedGenieConnectorsPromo = true;
	}

	// A Genie turn finished in some tab. No toast when the user is already looking at that thread: on its
	// route, in the open panel (`threadId` is the thread the panel shows, `urlThreadId` lags it), or on
	// the routine run page showing it.
	onGenieAnswerReady = (detail: GenieAnswerReadyDetail): void => {
		this.eventManagerService.trigger(AppEvent.GENIE_ANSWER_READY, detail);

		if (this.navigationMainService.activeThreadId === detail.threadId) {
			return;
		}

		if (this.isOpen && this.threadId === detail.threadId) {
			return;
		}

		if (this.getRunThreadId?.() === detail.threadId) {
			return;
		}

		this.infolayerService.showGenieAnswerReady(detail, (id) => {
			this.openThread(id);
		});
	};

	@action
	openThread(threadId: string): void {
		if (this.navigationService.isMobileApp) {
			// Seeded like applyUrlThread does on desktop, so Genie mounts straight into the thread.
			this.currentUrlThreadId = threadId;
			this.currentUrlArtifactId = null;
			this.mobileGenieController.threadId = threadId;
			this.navigationService.transitionToRoute('mobile.genie');
			return;
		}

		this.navigationService.transitionToRoute('genie.chat.thread', threadId);
	}

	// The side panel's expand button: its thread on the full page, or Genie's home before the first prompt.
	// Entering a Genie route closes the panel (navigation service), so nothing else needs tearing down.
	@action
	openInGenie(threadId: string | null): void {
		if (threadId) {
			this.openThread(threadId);
			return;
		}

		this.navigationService.transitionToRoute(
			this.navigationService.isMobileApp
				? `mobile.${AiChatRouteSource.GENIE}`
				: AiChatRouteSource.GENIE,
		);
	}

	@action
	onBookACall(origin: GenieBookACallOrigin): void {
		this.analyticsService.event(TrackingEvent.CALL_INITIATED, { origin });
		this.accountService.requestSetupHelp();
	}

	// The chat a mobile routine page was opened from: leaving mobile.genie.chat resets its thread, and /m has no thread
	// in the URL, so Back would otherwise land on a new chat.
	private routineReturnThreadId: string | null = null;

	private rememberRoutineReturnThread(): void {
		const route = this.navigationService.activatedRoute;
		if (route.startsWith('mobile.genie.routines')) {
			return;
		}

		this.routineReturnThreadId = route === 'mobile.genie.chat' ? this.currentUrlThreadId : null;
	}

	@action
	onNavigateToRoutines(): void {
		if (!this.navigationService.isMobileApp) {
			this.navigationService.transitionToRoute('routines');
			return;
		}

		if (this.routineReturnThreadId) {
			this.openThread(this.routineReturnThreadId);
			return;
		}

		this.navigationService.transitionToRoute('mobile.genie');
	}

	@action
	onNavigateToRoutine(routineId: string): void {
		this.rememberRoutineReturnThread();
		this.navigationService.transitionToRoute(
			this.navigationService.isMobileApp ? 'mobile.genie.routines.detail' : 'routines.detail',
			routineId,
		);
	}

	/**
	 * A Home → Recents run row opens that run's thread, not the routine it belongs to — the row is
	 * labelled with the routine's name, which makes the detail page the tempting wrong answer.
	 */
	@action
	onNavigateToRun(routineId: string, runId: string): void {
		this.rememberRoutineReturnThread();
		this.navigationService.transitionToRoute(
			this.navigationService.isMobileApp ? 'mobile.genie.routines.run' : 'routines.run',
			routineId,
			runId,
		);
	}

	@action
	showToast(type: ToastType, message: string): void {
		// The React side has no handle to dismiss a toast, so the spinner snackbar must expire on
		// its own — EXPIRE_PROGRESS matches the other long-running infolayer items.
		if (type === 'loading') {
			this.infolayerService.loading(message, EXPIRE_PROGRESS);
			return;
		}

		this.infolayerService[type](message);
	}

	// Captured from the app-level LineageExplorerHost; Genie's chart card opens the explorer through it.
	private openLineage: OpenLineageExplorer | null = null;

	@action
	registerLineageOpener(open: OpenLineageExplorer): void {
		this.openLineage = open;
	}

	// Clears the opener only if it is still the one the destroyed host registered.
	@action
	unregisterLineageOpener(open: OpenLineageExplorer): void {
		if (this.openLineage === open) {
			this.openLineage = null;
		}
	}

	private pendingVerifications = new Set<string>();

	// A repeat Verify before the save returns would write the card's stale state again: ignored.
	private async setMetricVerification(
		payload: VerifyMetricPayload,
	): Promise<VerifyMetricResult | undefined> {
		const pendingKey = `${payload.spaceAccessId}|${payload.key}`;

		if (this.pendingVerifications.has(pendingKey)) return;

		this.pendingVerifications.add(pendingKey);
		try {
			return await this.saveMetricVerification(payload);
		} finally {
			this.pendingVerifications.delete(pendingKey);
		}
	}

	// The metrics store fills on request, so a cold store is loaded once before giving up on the key.
	// Resolves the state the server saved, or nothing when the metric isn't found or the save fails.
	private async saveMetricVerification({
		key,
		spaceAccessId,
		isVerified,
	}: VerifyMetricPayload): Promise<VerifyMetricResult | undefined> {
		let metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(key, spaceAccessId);

		if (!metric) {
			await this.mmMetricsService.requestMetrics();
			metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(key, spaceAccessId);
		}

		if (!metric) {
			this.showToast('error', 'Metric is not available');
			return;
		}

		const saved = await this.mmMetricsService.setVerification(metric, isVerified);

		if (saved === undefined) {
			this.showToast('error', 'Metric cannot be verified');
			return;
		}

		// An unchanged state means the save failed and the verification service already told the user.
		if (saved === isVerified) return;

		return { isVerified: saved };
	}

	@action
	async onActionClick(action: ChatAction): Promise<Awaited<ReturnType<OnActionClick>>> {
		switch (action.type as ChatActionEnums | ChartActionEnums) {
			case ChatActionEnums.OPEN_DATABOARD_PREVIEW: {
				this.openBoardPreview(action.payload as OpenDataboardPreviewPayload);
				return;
			}
			case ChatActionEnums.OPEN_METRIC_INSIGHTS: {
				this.viewMetricInsights(
					action.payload as APIData.GenerativeAI.AiChat.MetricInsightsDrawerAction,
				);
				return;
			}
			case ChatActionEnums.SWITCH_TO_CLIENT_ACCOUNT: {
				this.switchToClientAccount(action.payload as SwitchToClientAccountPayload);
				return;
			}
			case ChatActionEnums.CONNECT_DATA_SOURCE: {
				const { sourceKey } = action.payload as ConnectDataSourcePayload;

				await this.connectService.connectSourceFromIdOrKey(sourceKey, {
					origin: this.trackingOrigin,
				});
				return;
			}
			case ChartActionEnums.ADD_TO_DATABOARD: {
				const { saved, targetLabel, boardId } = await this.openAddToDataboardPopup(
					action.payload as APIData.GenerativeAI.AiChat.OpenAddToDataboardPopupAction,
				);
				return {
					saved,
					targetLabel,
					target: {
						type: ChatActionEnums.OPEN_DATABOARD_PREVIEW,
						payload: { board: { id: Number(boardId), name: targetLabel, link: '' } },
					} as ChatAction,
				};
			}
			case ChartActionEnums.SET_NEW_GOAL: {
				const { saved, targetLabel, goalId, hasChanged } = await this.openSetNewGoalPopup(
					action.payload as APIData.GenerativeAI.AiChat.OpenSetNewGoalAction,
				);
				return {
					saved: saved && !hasChanged,
					targetLabel,
					target: {
						url: `${window.location.origin}/goals/insights/${goalId}`,
					},
				};
			}
			case ChartActionEnums.ADD_TO_SPACE: {
				const { saved, targetLabel, teamId } = await this.openAddToSpacePopup(
					action.payload as APIData.GenerativeAI.AiChat.OpenAddToSpacePopupAction,
				);
				return {
					saved,
					targetLabel,
					target: {
						url: `${window.location.origin}/teams/${teamId}`,
					},
				};
			}
			case ChartActionEnums.VERIFY_METRIC: {
				return this.setMetricVerification(action.payload as VerifyMetricPayload);
			}
			case ChartActionEnums.OPEN_LINEAGE: {
				const { key, spaceAccessId } = action.payload as OpenLineagePayload;
				const metricKey = this.mmMetricsService.getLineageKey(key, spaceAccessId);

				this.openLineage?.({ target: { metricKey } });
				return;
			}
			case ChartActionEnums.SCHEDULE_SNAPSHOT: {
				const { saved, targetLabel } = await this.openScheduleSnapshotPopup(
					action.payload as IframeActionPayload,
				);
				return {
					saved,
					targetLabel,
					target: {
						url: `${window.location.origin}/settings/notifications`,
					},
				};
			}
			case ChartActionEnums.ADD_TO_REPORT: {
				const { saved, targetLabel, reportId } = await this.openAddToReportPopup(
					action.payload as IframeActionPayload,
				);
				return {
					saved,
					targetLabel,
					target: {
						type: ChatActionEnums.OPEN_REPORT_PREVIEW,
						payload: { reportId: reportId! },
					} as unknown as ChatAction,
				};
			}
			case ChatActionEnums.OPEN_REPORT_PREVIEW: {
				const { reportId } = action.payload as unknown as { reportId: string };
				this.openReportPreview(reportId);
				return;
			}
			case ChartActionEnums.ADD_TO_LOOP: {
				const { saved, targetLabel, loopId } = await this.openAddToLoopPopup(
					action.payload as IframeActionPayload,
				);
				return {
					saved,
					targetLabel,
					target: {
						type: ChatActionEnums.OPEN_LOOP_PREVIEW,
						payload: { loopId: loopId! },
					} as unknown as ChatAction,
				};
			}
			case ChatActionEnums.OPEN_LOOP_PREVIEW: {
				const { loopId } = action.payload as unknown as { loopId: string };
				this.openLoopPreview(loopId);
				return;
			}
			case ChartActionEnums.SHARE_DATABOARD: {
				this.openShareDataboardPopup(action.payload as IframeActionPayload);
				return;
			}
			case ChatActionEnums.OPEN_GOAL: {
				const { goalId } = action.payload as GoalActionPayload;
				if (!(await this.mmGoalsService.openGoalInsightsDrawerById(goalId))) {
					this.showToast('error', GOAL_UNAVAILABLE_MESSAGE);
				}
				return;
			}
			case ChartActionEnums.EDIT_GOAL: {
				const { goalId } = action.payload as GoalActionPayload;
				const goal = await this.mmGoalsService.findOrRequestGoal(goalId);
				if (!goal) {
					this.showToast('error', GOAL_UNAVAILABLE_MESSAGE);
					return;
				}

				this.mmGoalsService.openEditPopup(
					goal,
					`${this.trackingOrigin}_${this.defaultTrackingPostfix}`,
				);
				return;
			}
			default:
				return;
		}
	}

	buildClientAssetDeeplink(asset: SwitchToClientAccountAsset): string {
		switch (asset.kind) {
			case CrossClientAssetType.DATABOARD:
				return `/databoards/view/${asset.boardId}/preview`;
			case CrossClientAssetType.GOAL:
				return `/goals/insights/${asset.goalId}`;
			case CrossClientAssetType.METRIC:
				return this.metricDetailsService.buildShareablePathFromOptions({
					metricSetup: new MetricSetup({ ...asset.metricSetup }),
				});
		}
	}

	private switchToClientAccount(payload: SwitchToClientAccountPayload): void {
		const { spaceId, asset } = payload;

		const deeplink = this.buildClientAssetDeeplink(asset);

		const params = new URLSearchParams();
		const sourceThreadId = this.threadId;
		if (sourceThreadId) {
			params.set(GENIE_THREAD_ID_PARAM, sourceThreadId);
			params.set(GENIE_FROM_SPACE_ID_PARAM, String(this.accountService.id));
		}

		const query = params.toString();
		const dest = query ? `${deeplink}${deeplink.includes('?') ? '&' : '?'}${query}` : deeplink;
		redirect(`/change/space/${spaceId}?redirect_path=${encodeURIComponent(dest)}`);
	}

	private close(): void {
		this.isOpen = false;
		this.intercomService.uncover(IntercomCover.GENIE_PANEL);
	}

	// Onboarding

	private createOnboardingGeniePopup() {
		return this.popupService.createModal(OnboardingPopupGenieComponent, {
			popupClass: 'popup-type-onboarding-genie',
			closeOnOverlayClick: false,
		});
	}

	private createOnboardingGenieArtifactsPopup() {
		return this.popupService.createModal(OnboardingPopupGenieArtifactsComponent, {
			popupClass: 'popup-type-onboarding-genie-artifacts',
			closeOnOverlayClick: false,
		});
	}

	private createAgenticIntroPopup() {
		// The only way out is one of the three CTAs inside the intro page - no close chip,
		// no overlay click, no ESC (see popup-agentic-intro.gts).
		return this.popupService.createModal(OnboardingPopupAgenticIntroComponent, {
			popupClass: 'popup-type-agentic-intro no-edges',
			showClose: false, // also disables ESC, see app-loaded-events.gts
			closeOnOverlayClick: false,
		});
	}

	// QA / preview trigger, from the browser console: `debug('ai-chat').openAgenticIntroPopupPreview()`.
	// Bypasses the automated queue and its launch / eligibility / seen checks, and the
	// preview neither marks the intro as seen nor tracks.
	openAgenticIntroPopupPreview(): void {
		this.createAgenticIntroPopup().open({ preview: true });
	}

	addModalToAutomatedPopupQueue(): void {
		const { isViewer, isDemo, model: currentUser } = this.currentUserService;
		const userCreatedTS = currentUser?.created;
		const showForOldUsers = !!userCreatedTS && userCreatedTS < getTs('2026-03-18 08:30:00');

		// Queued first: this is the one-time "first entry after launch" announcement, so it
		// must not wait behind the older Genie popups a user may not have dismissed yet.
		// Only for users who signed up before the launch AND had already finished onboarding
		// when they entered the app - someone still mid-onboarding must not get it.
		const showAgenticIntroForOldUsers =
			wasCreatedBeforeAgenticLaunch(userCreatedTS) && this.currentUserService.isOnboarded;

		this.automatedPopupQueueService.queuePopup({
			popup: this.createAgenticIntroPopup(),
			checkAddConditions: () => {
				return (
					!this.agenticIntroAttempted &&
					this.isAiChatAvailable &&
					showAgenticIntroForOldUsers &&
					!isViewer &&
					!isDemo &&
					!this.navigationService.isMobileApp
				);
			},
			// Re-checked on every route change, so a tab left open across the launch
			// moment gets the intro on its next navigation.
			checkStartConditions: () => {
				const shouldStart =
					!this.agenticIntroAttempted &&
					hasAgenticIntroLaunched() &&
					!this.userSettingsService.hasSeenAgenticIntroPopup &&
					window.innerWidth >= MD_BREAKPOINT;

				if (shouldStart) {
					// The queue opens the popup right after this returns true.
					this.agenticIntroAttempted = true;
				}

				return shouldStart;
			},
			skipCooldown: true,
		});

		this.automatedPopupQueueService.queuePopup({
			popup: this.createOnboardingGeniePopup(),
			checkAddConditions: () => {
				return (
					this.isAiChatAvailable &&
					showForOldUsers &&
					!isViewer &&
					!isDemo &&
					!this.navigationService.isMobileApp
				);
			},
			checkStartConditions: () => {
				return (
					!this.userSettingsService.hasSeenOnboardingGeniePopup &&
					window.innerWidth >= MD_BREAKPOINT
				);
			},
			skipCooldown: true,
		});

		this.automatedPopupQueueService.queuePopup({
			popup: this.createOnboardingGenieArtifactsPopup(),
			checkAddConditions: () => {
				return (
					this.isAiChatAvailable &&
					!isViewer &&
					!isDemo &&
					!this.navigationService.isMobileApp
				);
			},
			checkStartConditions: () => {
				return (
					!this.userSettingsService.hasSeenOnboardingGenieArtifactsPopup &&
					window.innerWidth >= MD_BREAKPOINT
				);
			},
			skipCooldown: true,
		});
	}
}

declare module '@ember/service' {
	interface Registry {
		'ai-chat': AiChatService;
	}
}
